import {XIcon} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef} from 'react'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {useDescriptionLiveUpdates} from '../../hooks/use-description-live-updates'

type OverviewSidePanelProps = {
  aliveChannel: string
  onClose: () => void
}

/**
 * Renders the overview side panel for pull requests
 */
export function OverviewSidePanel({aliveChannel, onClose}: OverviewSidePanelProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const toggleSidesheetRef = useRef<HTMLButtonElement>(null)

  // eslint-disable-next-line github/unescaped-html-literal -- Known safe static fallback content
  const emptyDescription = `<p class="text-italic">No description provided.</p>` as SafeHTMLString

  const {data: description, isLoading} = useDescriptionData()
  useDescriptionLiveUpdates(aliveChannel)
  const bodyHtml = (description?.bodyHtml || emptyDescription) as SafeHTMLString

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header className="p-3">
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full">
            <Dialog.Title id={dialogLabelId}>Overview</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close overview panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
        </Dialog.Header>
      )}
    >
      {isLoading ? (
        <div className="d-flex flex-justify-center p-4">
          <Spinner />
        </div>
      ) : (
        <div className="d-flex flex-justify-center">
          <NewMarkdownViewer verifiedHTML={bodyHtml} />
        </div>
      )}
    </Dialog>
  )
}

try{ OverviewSidePanel.displayName ||= 'OverviewSidePanel' } catch {}